import axios from 'axios';
import type { User, Project, Timesheet, PaginatedResponse, DashboardStats, Notification, PtoMine, PtoRequest, PtoReviewItem, PtoTeamMember, PtoTeamRequest, PtoTimesheetDay } from '@/types';

const API_URL = (import.meta as any).env?.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = [];

const processQueue = (error: unknown = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Response interceptor for handling 401 errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh for auth endpoints (login, refresh, etc.)
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        await api.post('/auth/refresh');
        processQueue();
        isRefreshing = false;
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        isRefreshing = false;
        
        // Refresh failed - clear auth state and redirect to login
        // Clear the persisted auth state
        localStorage.removeItem('qs-tempo-auth');
        
        // Show a message and redirect
        if (typeof window !== 'undefined') {
          // Use a small delay to allow any pending operations to complete
          setTimeout(() => {
            window.location.href = '/login?session=expired';
          }, 100);
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  verifyOtp: (pendingToken: string, otp: string) =>
    api.post('/auth/verify-otp', { pending_token: pendingToken, otp }),
  
  logout: () => api.post('/auth/logout'),
  
  refresh: () => api.post('/auth/refresh'),
  
  getMe: () => api.get<User>('/auth/me'),
  
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (email: string, otp: string, newPassword: string) =>
    api.post('/auth/reset-password', { email, otp, new_password: newPassword }),
};

// Users API
export const usersApi = {
  list: (params?: object) =>
    api.get<PaginatedResponse<User>>('/users', { params }),
  
  getResources: () =>
    api.get<User[]>('/users/resources'),
  
  get: (id: number) =>
    api.get<User>(`/users/${id}`),
  
  create: (data: object) =>
    api.post<User>('/users', data),
  
  update: (id: number, data: object) =>
    api.put<User>(`/users/${id}`, data),
  
  delete: (id: number) =>
    api.delete(`/users/${id}`),
  
  updateProfile: (data: object) =>
    api.put<User>('/users/profile', data),
  
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/users/change-password', data),
  
  resetPassword: (userId: number) =>
    api.post(`/users/${userId}/reset-password`),
  
  unlock: (userId: number) =>
    api.post(`/users/${userId}/unlock`),
};

// Projects API
export const projectsApi = {
  list: (params?: object) =>
    api.get<PaginatedResponse<Project>>('/projects', { params }),
  
  getMyAssigned: () =>
    api.get<Project[]>('/projects/my-assigned'),
  
  get: (id: number) =>
    api.get<Project>(`/projects/${id}`),
  
  create: (data: object) =>
    api.post<Project>('/projects', data),
  
  update: (id: number, data: object) =>
    api.put<Project>(`/projects/${id}`, data),
  
  addAssignment: (projectId: number, data: object) =>
    api.post(`/projects/${projectId}/assignments`, data),
  
  removeAssignment: (projectId: number, resourceId: number) =>
    api.delete(`/projects/${projectId}/assignments/${resourceId}`),
  
  assignResource: (projectId: number, resourceId: number) =>
    api.post(`/projects/${projectId}/assignments`, { resource_id: resourceId }),
  
  removeResource: (projectId: number, resourceId: number) =>
    api.delete(`/projects/${projectId}/assignments/${resourceId}`),
};

// Timesheets API
export const timesheetsApi = {
  list: (params?: object) =>
    api.get<PaginatedResponse<Timesheet>>('/timesheets', { params }),
  
  getMyTimesheets: (params?: object) =>
    api.get<Timesheet[]>('/timesheets/my', { params }),
  
  getPendingApprovals: () =>
    api.get<Timesheet[]>('/timesheets/pending-approvals'),
  
  get: (id: number) =>
    api.get<Timesheet>(`/timesheets/${id}`),
  
  create: (data: object) =>
    api.post<Timesheet>('/timesheets', data),
  
  update: (id: number, data: object) =>
    api.put<Timesheet>(`/timesheets/${id}`, data),
  
  submit: (id: number) =>
    api.post<Timesheet>(`/timesheets/${id}/submit`),
  
  approve: (id: number, data?: object) =>
    api.post<Timesheet>(`/timesheets/${id}/approve`, data || {}),
  
  reject: (id: number, comments: string) =>
    api.post<Timesheet>(`/timesheets/${id}/reject`, { comments }),
  
  requestChanges: (id: number, comments: string) =>
    api.post<Timesheet>(`/timesheets/${id}/request-changes`, { comments }),
  
  delete: (id: number) =>
    api.delete(`/timesheets/${id}`),
  
  uploadAttachment: (timesheetId: number, file: File, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    return api.post(`/timesheets/${timesheetId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  getAttachments: (timesheetId: number) =>
    api.get(`/timesheets/${timesheetId}/attachments`),
  
  deleteAttachment: (timesheetId: number, attachmentId: number) =>
    api.delete(`/timesheets/${timesheetId}/attachments/${attachmentId}`),
};

// PTO API
export const ptoApi = {
  getMine: () =>
    api.get<PtoMine>('/pto/me'),

  createRequest: (data: object) =>
    api.post<PtoRequest>('/pto/requests', data),

  cancelRequest: (id: number) =>
    api.post<PtoRequest>(`/pto/requests/${id}/cancel`),

  getReview: () =>
    api.get<PtoReviewItem[]>('/pto/review'),

  approve: (id: number, data: { is_paid: boolean; allow_negative: boolean }) =>
    api.post<PtoRequest>(`/pto/requests/${id}/approve`, data),

  decline: (id: number, note: string) =>
    api.post<PtoRequest>(`/pto/requests/${id}/decline`, { note }),

  getTeam: () =>
    api.get<PtoTeamMember[]>('/pto/team'),

  getTeamRequests: () =>
    api.get<PtoTeamRequest[]>('/pto/requests'),

  updateMember: (userId: number, data: { pto_eligible?: boolean; pto_hours_per_month?: number; pto_start_date?: string }) =>
    api.put(`/pto/team/${userId}`, data),

  /** What a start date would add (+) or take back (-) now, without saving it */
  previewStartDate: (userId: number, startDate: string) =>
    api.put<{ preview: { hours: number; from: string | null; to: string | null } }>(`/pto/team/${userId}`, { pto_start_date: startDate, preview: true }),

  adjust: (userId: number, data: { hours: number; note: string }) =>
    api.post(`/pto/team/${userId}/adjust`, data),

  timesheetDays: (params: { project_id: number; period_start: string; period_end: string; timesheet_id?: number }) =>
    api.get<PtoTimesheetDay[]>('/pto/timesheet-days', { params }),
};

// Dashboard API
export const dashboardApi = {
  getStats: (period?: string) =>
    api.get<DashboardStats>('/dashboard/stats', { params: { period } }),
  
  getRecentTimesheets: (limit?: number) =>
    api.get('/dashboard/recent-timesheets', { params: { limit } }),
  
  getSummaryStats: (period?: string) =>
    api.get('/dashboard/summary-stats', { params: { period } }),
  
  getProjectStats: (period?: string) =>
    api.get('/dashboard/project-stats', { params: { period } }),
  
  getWeeklyTrend: (weeks?: number) =>
    api.get('/dashboard/weekly-trend', { params: { weeks } }),
  
  getCostByProject: (period?: string) =>
    api.get('/dashboard/cost-by-project', { params: { period } }),
};

// Notifications API
export const notificationsApi = {
  list: (params?: object) =>
    api.get<{ items: Notification[]; unread_count: number }>('/notifications', { params }),
  
  markAsRead: (ids: number[]) =>
    api.post('/notifications/mark-read', ids),
  
  markAllAsRead: () =>
    api.post('/notifications/mark-all-read'),

  sendReminders: () =>
    api.post('/notifications/send-reminders'),

  sendDigest: () =>
    api.post('/notifications/send-digest'),
};

// Financials API
export const financialsApi = {
  getFinancials: (params?: {
    start_date?: string;
    end_date?: string;
    project_id?: number;
    resource_id?: number;
    status?: string;
  }) =>
    api.get('/financials', { params }),
};

export const payrollCalculatorApi = {
  calculate: (payload: {
    hourly_rate: number;
    zip_code: string;
    hours_per_week?: number;
    weeks_per_year?: number;
  }) => api.post('/payroll-calculator/calculate', payload),
};

export interface ExpiringDocument {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  document_type: 'driver_license' | 'work_authorization' | 'permit' | 'health_insurance';
  expires_on: string;
  days_until: number;
  expired: boolean;
}

export const complianceApi = {
  expiringDocuments: (withinDays = 30) =>
    api.get<ExpiringDocument[]>('/users/expiring-documents', {
      params: { within_days: withinDays },
    }),
};

export default api;

